<template>
  <div class="match-insights px-1 pb-2">
    <p class="text-xxs text-brandGrey text-center mb-2">
      <template v-if="mode === 'value'"
        >Bets where a bookmaker is paying more than we think they're worth.</template
      >
      <template v-else
        >Our strongest picks. The value shows here once bookmakers price the match.</template
      >
    </p>
    <p v-if="!displayRows.length" class="text-xs text-center text-gray-500 py-2">
      No standout bets in this match yet.
    </p>
    <div class="grid gap-1.5">
      <template v-for="row in displayRows">
      <p
        v-if="row.heading"
        :key="`h-${row.selection.id}`"
        class="text-xxs uppercase tracking-wide font-semibold text-gray-500 text-left mt-1"
      >
        {{ row.heading }}
      </p>
      <div
        :key="row.selection.id"
        class="insight-row grid items-center gap-2.5 bg-white border border-gray-300 px-2.5 py-2"
      >
        <span
          class="self-stretch w-1 rounded-sm"
          :class="{
            'bg-[#E59C98]': row.selection.side === 'home',
            'bg-[#BDBDFB]': row.selection.side === 'away',
            'bg-gray-300': !row.selection.side,
          }"
          aria-hidden="true"
        ></span>
        <div class="min-w-0 text-left">
          <div class="text-sm font-semibold text-gray-800 truncate">{{ row.selection.label }}</div>
          <div class="text-xxs text-gray-500" :class="{ 'blur-sm select-none': locked }">
            {{ locked ? "we make it ??%" : row.odds ? `we make it ${Math.round(row.p)}% · fair ${format(100 / row.p)}` : `we make it ${Math.round(row.p)}% · no price yet` }}
          </div>
        </div>
        <div v-if="row.odds" class="text-right text-xxs text-gray-500 tabular-nums">
          {{ row.bookmaker || "best odds" }}
          <b class="block text-sm font-semibold text-gray-800">{{ format(row.odds) }}</b>
        </div>
        <div v-else class="text-right text-xxs text-gray-500 tabular-nums" :class="{ 'blur-sm select-none': locked }">
          fair
          <b class="block text-sm font-semibold text-gray-800">{{ locked ? "?.??" : format(100 / row.p) }}</b>
        </div>
        <span
          v-if="row.edge !== null"
          class="chip"
          :class="locked ? 'neutral blur-sm' : edgeClass(row.edge)"
          >{{ locked ? "??" : formatEdge(row.edge) }}</span
        >
        <span v-else aria-hidden="true"></span>
        <AddPill :selection="row.selection" />
      </div>
      </template>
    </div>
    <p v-if="locked && displayRows.length" class="text-center text-xs mt-2">
      <router-link
        to="/signup/"
        class="text-blue-700 underline uppercase font-bold"
        v-on:click.native="upgradeClick"
        >Upgrade to see our numbers →</router-link
      >
    </p>
  </div>
</template>

<script>
import AddPill from "./AddPill.vue";
import { bestResultPrice, bestGoalPrice } from "./bestPrice";
import { formatOdds, formatEdge, edgeClass } from "../../odds";
import { trackFeature } from "../../analytics";

const FREE_LEAGUES = [8];
const MIN_EDGE = 3;
// value lists drift to long shots (that is where models and bookmakers disagree most); only
// count bets we give a realistic chance
const MIN_P = 30;
const MAX_ROWS = 5;
const SIDE_KEYS = { home: "home_team", draw: "draw", away: "away_team" };
const PLAYER_LABELS = {
  sot: (n, l) => `${n} ${l} ${l === "1+" ? "shot" : "shots"} on target`,
  shots: (n, l) => `${n} ${l} shots`,
  fouls: (n, l) => `${n} ${l} ${l === "1+" ? "foul" : "fouls"}`,
  tackles: (n, l) => `${n} ${l} ${l === "1+" ? "tackle" : "tackles"}`,
  goal: (n) => `${n} anytime goalscorer`,
  card: (n) => `${n} to be booked`,
};

function surname(name) {
  const parts = (name || "").split(" ");
  return parts.length > 1 ? parts[parts.length - 1] : name || "";
}

export default {
  name: "MatchInsights",
  components: { AddPill },
  props: {
    match: { type: Object, required: true }, // the list card
    info: { type: Object, default: null }, // match-info (props, markets)
    slipMatch: { type: Object, required: true },
  },
  computed: {
    member() {
      return this.$store.getters.subscriptionType === "active";
    },
    locked() {
      return !this.member && !FREE_LEAGUES.includes(this.match.league_id);
    },
    oddsFormat() {
      return this.$store.getters.preferences.odds_format;
    },
    candidates() {
      return [...this.teamCandidates(), ...this.playerCandidates()].filter(
        (c) => c.p !== null && c.p !== undefined && c.p > 0,
      );
    },
    mode() {
      return this.valueRows.length >= 2 ? "value" : "likely";
    },
    // best value: priced bets, best edge first, one line per player and market
    valueRows() {
      const priced = this.candidates
        .filter((c) => c.odds)
        .map((c) => ({ ...c, edge: (c.p / 100) * c.odds * 100 - 100 }))
        .filter((c) => c.edge >= MIN_EDGE && c.p >= MIN_P)
        .sort((a, b) => b.edge - a.edge);
      return this.pick(priced);
    },
    // before prices: the most likely bets that are not near-certainties
    likelyRows() {
      const likely = this.candidates
        .filter((c) => c.p >= 45 && c.p <= 85)
        .map((c) => ({ ...c, edge: c.odds ? (c.p / 100) * c.odds * 100 - 100 : null }))
        .sort((a, b) => b.p - a.p);
      return this.pick(likely, 1);
    },
    rows() {
      return this.mode === "value" ? this.valueRows : this.likelyRows;
    },
    // the match's best-priced team bet (result, BTTS, goals), shown with its true value even when
    // it is only fair, so the result always gets a line; green only when it really is value
    teamRow() {
      const priced = this.candidates
        .filter((c) => !c.player && c.odds && c.p >= MIN_P)
        .map((c) => ({ ...c, edge: (c.p / 100) * c.odds * 100 - 100 }))
        .sort((a, b) => b.edge - a.edge);
      return priced[0] || null;
    },
    displayRows() {
      if (this.mode !== "value") return this.rows;
      const rows = this.rows.map((r, i) => (i === 0 ? { ...r, heading: "Best value" } : r));
      const team = this.teamRow;
      if (!team || this.rows.some((r) => r.selection.id === team.selection.id)) return rows;
      return [{ ...team, heading: "Best team bet" }, ...rows];
    },
  },
  methods: {
    upgradeClick() {
      trackFeature("betslip_upgrade_click", "betslip", { from: "value_picks" });
    },
    format(decimal) {
      return formatOdds(decimal, this.oddsFormat);
    },
    formatEdge,
    edgeClass,
    selection(market, subject, line, label, side) {
      return {
        id: `${this.slipMatch.match_id}|${market}|${subject}|${line}`,
        label,
        match: this.slipMatch.title,
        match_id: this.slipMatch.match_id,
        kickoff: this.slipMatch.kickoff,
        side: side || null,
      };
    },
    // at most one line per player per market and two bets per player, so the list mixes markets
    pick(list, perMarket = 0) {
      const seen = {};
      const perPlayer = {};
      const marketCount = {};
      const out = [];
      for (const c of list) {
        if (perMarket && (marketCount[c.market] || 0) >= perMarket) continue;
        // team markets: one side per market (never both over and under); players: one line per market
        const key = c.player ? `${c.group}|${c.market}` : c.group;
        if (seen[key]) continue;
        if (c.player && (perPlayer[c.group] || 0) >= 2) continue;
        seen[key] = true;
        marketCount[c.market] = (marketCount[c.market] || 0) + 1;
        if (c.player) perPlayer[c.group] = (perPlayer[c.group] || 0) + 1;
        out.push(c);
        if (out.length >= MAX_ROWS) break;
      }
      return out;
    },
    priced(price) {
      return { odds: price ? price.odds : null, bookmaker: price ? price.bookmaker : null };
    },
    prediction(key) {
      const p = this.match.predictions || {};
      const value = p[key] !== undefined ? p[key] : (p.sport_monks || {})[key];
      return value === null || value === undefined ? null : Number(value);
    },
    teamCandidates() {
      const card = this.match;
      const markets = (this.info && this.info.markets) || card.markets || {};
      const out = [];
      for (const subject of ["home", "draw", "away"]) {
        const block = card[SIDE_KEYS[subject]];
        if (!block) continue;
        const label = subject === "draw" ? "Draw" : `${block.name} to win`;
        const price = bestResultPrice(card, subject);
        out.push({
          group: "1x2", market: "1x2", p: block.prediction, ...this.priced(price),
          selection: this.selection("1x2", subject, "", label, subject === "draw" ? null : subject),
        });
      }
      // prices are the best any bookmaker offers, as on the slip
      const btts = markets.btts || {};
      const bttsYes = btts.yes ? btts.yes.p : this.prediction("btts");
      out.push({
        group: "btts", market: "btts", p: bttsYes, ...this.priced(bestGoalPrice(card, "btts", "yes")),
        selection: this.selection("btts", "yes", "", "Both teams to score"),
      });
      if (bttsYes !== null) {
        out.push({
          group: "btts", market: "btts-no", p: btts.no ? btts.no.p : 100 - bttsYes,
          ...this.priced(bestGoalPrice(card, "btts", "no")),
          selection: this.selection("btts", "no", "", "Both teams to score: no"),
        });
      }
      for (const line of ["2.5", "3.5"]) {
        const key = `ou_${line.replace(".", "_")}`;
        const priced = markets[key] || {};
        const over = priced.over ? priced.over.p : this.prediction(`FT_over_${line.replace(".", "_")}`);
        if (over === null) continue;
        out.push({
          group: key, market: "over", p: over, ...this.priced(bestGoalPrice(card, key, "over")),
          selection: this.selection("ou", "over", line, `Over ${line} goals`),
        });
        out.push({
          group: key, market: "under", p: priced.under ? priced.under.p : 100 - over,
          ...this.priced(bestGoalPrice(card, key, "under")),
          selection: this.selection("ou", "under", line, `Under ${line} goals`),
        });
      }
      return out;
    },
    playerCandidates() {
      const props = (this.info && this.info.props) || {};
      const out = [];
      for (const side of ["home_team", "away_team"]) {
        for (const row of props[side] || []) {
          if (row.status === "injured" || row.status === "suspended" || !row.minutes_exp) continue;
          const name = surname(row.name);
          for (const [market, lines] of Object.entries(row.markets || {})) {
            for (const [line, price] of Object.entries(lines)) {
              if (price.p === null || price.p === undefined) continue;
              out.push({
                // player props are priced by bet365 only
                group: `p${row.player_id}`, market, player: true, p: price.p, odds: price.odds || null,
                bookmaker: price.odds ? "bet365" : null,
                selection: this.selection(market, row.player_id, line, PLAYER_LABELS[market](name, line),
                  side === "home_team" ? "home" : "away"),
              });
            }
          }
        }
      }
      return out;
    },
  },
};
</script>

<style scoped>
.insight-row {
  grid-template-columns: 4px minmax(0, 1fr) auto auto auto;
}
</style>
